import { API_URL } from "../config/api";
import { validateIllustrationDataUrl } from "./illustrationSanitize";

export const ILLUSTRATION_ACCEPT = "image/jpeg,image/png,image/webp";
export const ILLUSTRATION_MAX_SIDE = 300;
export const ILLUSTRATION_WEBP_QUALITY = 0.8;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Impossible de lire l'image sélectionnée."));
    };

    image.src = objectUrl;
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Impossible de convertir l'image."));
    reader.readAsDataURL(blob);
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Conversion de l'image impossible."));
      },
      mimeType,
      quality
    );
  });
}

export async function prepareWordIllustration(file) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Veuillez sélectionner une image.");
  }

  if (!ILLUSTRATION_ACCEPT.split(",").includes(file.type)) {
    throw new Error("Formats acceptés : JPEG, PNG ou WebP.");
  }

  const image = await loadImageFromFile(file);
  const longestSide = Math.max(image.width, image.height, 1);
  const scale = Math.min(1, ILLUSTRATION_MAX_SIDE / longestSide);
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Conversion de l'image impossible.");
  }

  context.drawImage(image, 0, 0, width, height);

  let blob;
  try {
    blob = await canvasToBlob(canvas, "image/webp", ILLUSTRATION_WEBP_QUALITY);
  } catch {
    blob = await canvasToBlob(canvas, "image/jpeg", ILLUSTRATION_WEBP_QUALITY);
  }

  return blobToDataUrl(blob);
}

export async function fetchWordIllustration(mot) {
  const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/illustration`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || "Impossible de charger l'illustration.");
  }

  return data.dataUrl || null;
}

export async function uploadWordIllustration(mot, dataUrl) {
  const validation = validateIllustrationDataUrl(dataUrl);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/illustration`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dataUrl }),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || "Impossible d'enregistrer l'illustration.");
  }

  return data.dataUrl || dataUrl;
}

export async function deleteWordIllustration(mot) {
  const res = await fetch(`${API_URL}/mots/${encodeURIComponent(mot)}/illustration`, {
    method: "DELETE",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || "Impossible de supprimer l'illustration.");
  }
}
